import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { PaymentMethod } from "../../Enums/RegistrationEnum";
import type { IEvent } from "../../interfaces/models/IEvent";
import type { IRegistration, IRegistrationGuest } from "../../interfaces/models/IRegistration";
import type { IPayment } from "../../interfaces/models/IPayment";
import IFirm from "../../interfaces/models/IFirm";
import type { AuditLog } from "../../interfaces/models/IAudityLogs";
import type { SpecialEventGuestsMap } from "../../utils/specialEventGuests";
import {
  EditParticipantEmpty,
  type EditParticipantPersistedPaymentFields,
  type EditParticipantState,
} from "../emptyStates/EditParticipant";
import type { PaymentHistoryRow, RegistrantHistoryRow } from "../../pages/Events/EditParticipant/types";
import type { RegistrantCatalogOption } from "../../pages/Events/EditParticipant/hooks/useRegistrantCatalogOptions";

type InitPayload = {
  eventId: string;
  registrationId?: string;
  isEditMode: boolean;
};

export const editParticipantSlice = createSlice({
  name: "editParticipant",
  initialState: EditParticipantEmpty,
  reducers: {
    initEditParticipant: (state, action: PayloadAction<InitPayload>) => {
      const { eventId, registrationId, isEditMode } = action.payload;
      state.meta.eventId = eventId;
      state.meta.registrationId = registrationId;
      state.meta.isEditMode = isEditMode;
      state.meta.activeTab = 0;
      state.meta.hasSavedOnce = false;
      state.meta.tabs = ["REGISTRATION", "SEND EMAIL INVOICE"];
      state.form.isRegistrantModifyMode = !isEditMode;
      state.form.autoPrepaidLocked = false;
      state.audit.auditHistoryExpanded = false;
    },
    ResetEditParticipant: () => EditParticipantEmpty,
    setActiveTab: (state, action: PayloadAction<number>) => {
      state.meta.activeTab = action.payload;
    },
    setHasSavedOnce: (state, action: PayloadAction<boolean>) => {
      state.meta.hasSavedOnce = action.payload;
    },
    setTabs: (state, action: PayloadAction<string[]>) => {
      state.meta.tabs = action.payload;
    },
    setDataLoading: (state, action: PayloadAction<boolean>) => {
      state.data.loading = action.payload;
    },
    setEventData: (state, action: PayloadAction<IEvent | undefined>) => {
      state.data.eventData = action.payload;
    },
    setRegistrationData: (state, action: PayloadAction<IRegistration | undefined>) => {
      state.data.registrationData = action.payload;
    },
    patchRegistrationData: (state, action: PayloadAction<Partial<IRegistration>>) => {
      if (state.data.registrationData) {
        state.data.registrationData = {
          ...state.data.registrationData,
          ...action.payload,
        };
      }
    },
    setPaymentData: (state, action: PayloadAction<IPayment | undefined>) => {
      state.data.paymentData = action.payload;
    },
    setPayments: (state, action: PayloadAction<IPayment[]>) => {
      state.data.payments = action.payload;
    },
    setFirmData: (state, action: PayloadAction<IFirm | undefined>) => {
      state.data.firmData = action.payload;
    },
    setIsRegistrantModifyMode: (state, action: PayloadAction<boolean>) => {
      state.form.isRegistrantModifyMode = action.payload;
    },
    setGuests: (state, action: PayloadAction<IRegistrationGuest[]>) => {
      state.form.guests = action.payload;
    },
    setSelectedSpecialEventIds: (state, action: PayloadAction<string[]>) => {
      state.form.selectedSpecialEventIds = action.payload;
    },
    setSpecialEventGuestsMap: (state, action: PayloadAction<SpecialEventGuestsMap>) => {
      state.form.specialEventGuestsMap = action.payload;
    },
    setAdditionalFeeInput: (state, action: PayloadAction<string>) => {
      state.form.additionalFeeInput = action.payload;
    },
    setNewPaymentDateMs: (state, action: PayloadAction<number | null>) => {
      state.form.newPaymentDateMs = action.payload;
    },
    setNewPaymentAmount: (state, action: PayloadAction<string>) => {
      state.form.newPaymentAmount = action.payload;
    },
    setNewPaymentMethod: (state, action: PayloadAction<PaymentMethod | "">) => {
      state.form.newPaymentMethod = action.payload;
    },
    setNewPaymentNote: (state, action: PayloadAction<string>) => {
      state.form.newPaymentNote = action.payload;
    },
    setRefunded: (state, action: PayloadAction<boolean>) => {
      state.form.refunded = action.payload;
    },
    setAmountRefunded: (state, action: PayloadAction<string>) => {
      state.form.amountRefunded = action.payload;
    },
    setApplyFirmCredit: (state, action: PayloadAction<boolean>) => {
      state.form.applyFirmCredit = action.payload;
    },
    setCreditPayment: (state, action: PayloadAction<string>) => {
      state.form.creditPayment = action.payload;
    },
    setCcName: (state, action: PayloadAction<string>) => {
      state.form.ccName = action.payload;
    },
    setCcEmail: (state, action: PayloadAction<string>) => {
      state.form.ccEmail = action.payload;
    },
    setShowGuestValidationErrors: (state, action: PayloadAction<boolean>) => {
      state.form.showGuestValidationErrors = action.payload;
    },
    setAutoPrepaidLocked: (state, action: PayloadAction<boolean>) => {
      state.form.autoPrepaidLocked = action.payload;
    },
    setPersistedPaymentFields: (
      state,
      action: PayloadAction<EditParticipantPersistedPaymentFields>,
    ) => {
      state.form.persistedPaymentFields = action.payload;
    },
    syncFormFromRegistration: (state, action: PayloadAction<IRegistration>) => {
      const registration = action.payload;
      state.form.guests = registration.guests ?? [];
      state.form.selectedSpecialEventIds = Array.isArray(
        registration.selectedSpecialEventIds,
      )
        ? registration.selectedSpecialEventIds.filter(Boolean)
        : [];
      state.form.specialEventGuestsMap =
        registration.specialEventGuests ?? {};
      state.form.additionalFeeInput =
        registration.additionalFee != null
          ? String(registration.additionalFee)
          : "";
      state.form.newPaymentMethod =
        (registration.paymentMethod as PaymentMethod | undefined) ?? "";
      state.form.persistedPaymentFields = {
        amountPaid: registration.amountPaid,
        additionalFee: registration.additionalFee,
        paymentMethod: registration.paymentMethod,
        paymentStatus: registration.paymentStatus,
      };
    },
    clearPaymentDrafts: (state) => {
      state.form.newPaymentAmount = "";
      state.form.newPaymentNote = "";
      state.form.newPaymentMethod = "";
    },
    setAuditLogs: (state, action: PayloadAction<AuditLog[]>) => {
      state.audit.auditLogs = action.payload;
    },
    setAuditHistoryExpanded: (state, action: PayloadAction<boolean>) => {
      state.audit.auditHistoryExpanded = action.payload;
    },
    setIsLoadingAuditLogs: (state, action: PayloadAction<boolean>) => {
      state.audit.isLoadingAuditLogs = action.payload;
    },
    setRegistrantHistoryRows: (
      state,
      action: PayloadAction<RegistrantHistoryRow[]>,
    ) => {
      state.audit.registrantHistoryRows = action.payload;
    },
    setPaymentHistoryRows: (state, action: PayloadAction<PaymentHistoryRow[]>) => {
      state.audit.paymentHistoryRows = action.payload;
    },
    setIsSaving: (state, action: PayloadAction<boolean>) => {
      state.save.isSaving = action.payload;
    },
    setCatalogState: (
      state,
      action: PayloadAction<{
        organizationLabel?: string;
        organizationOptions?: RegistrantCatalogOption[];
        registrantOptions?: RegistrantCatalogOption[];
      }>,
    ) => {
      state.catalog = { ...state.catalog, ...action.payload };
    },
    replaceEditParticipantState: (
      _state,
      action: PayloadAction<EditParticipantState>,
    ) => action.payload,
  },
});

export const {
  initEditParticipant,
  ResetEditParticipant,
  setActiveTab,
  setHasSavedOnce,
  setTabs,
  setDataLoading,
  setEventData,
  setRegistrationData,
  patchRegistrationData,
  setPaymentData,
  setPayments,
  setFirmData,
  setIsRegistrantModifyMode,
  setGuests,
  setSelectedSpecialEventIds,
  setSpecialEventGuestsMap,
  setAdditionalFeeInput,
  setNewPaymentDateMs,
  setNewPaymentAmount,
  setNewPaymentMethod,
  setNewPaymentNote,
  setRefunded,
  setAmountRefunded,
  setApplyFirmCredit,
  setCreditPayment,
  setCcName,
  setCcEmail,
  setShowGuestValidationErrors,
  setAutoPrepaidLocked,
  setPersistedPaymentFields,
  syncFormFromRegistration,
  clearPaymentDrafts,
  setAuditLogs,
  setAuditHistoryExpanded,
  setIsLoadingAuditLogs,
  setRegistrantHistoryRows,
  setPaymentHistoryRows,
  setIsSaving,
  setCatalogState,
  replaceEditParticipantState,
} = editParticipantSlice.actions;

export default editParticipantSlice.reducer;
